import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  Button, Card, Col, Form, Row, Table, Spinner,
} from "react-bootstrap";
import { FaFileExcel, FaEye, FaMoneyBillWave, FaShoppingCart, FaLayerGroup } from "react-icons/fa";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend,
} from "recharts";
import axiosInstance from "../../axiosInstance";
import AdLayout from "../../components/AdLayout";
import { Pagination } from "../../components/ui";
import CheckingReview from "../../components/orders/CheckingReview";
import TthReconciliationReview from "../../components/orders/TthReconciliationReview";
import ReportSummaryCard from "./components/ReportSummaryCard";
import PeriodCompareLineChart from "./components/PeriodCompareLineChart";
import { PERIOD_OPTIONS, buildPeriodParams, getPeriodLabel } from "./utils/periodFilter";
import { buildChips, countActiveChips, formatDateChip, resolveOptionLabel } from "./utils/accountingFilterUtils";
import AccountingFilterSection from "./components/AccountingFilterSection";
import AccountingActiveFilters from "./components/AccountingActiveFilters";
import { formatVND as fmtVND, formatMoney } from "./utils/moneyFormat";
import "./accountingFilters.css";
import "./accountingResponsive.css";

const LINE_CARD_COLORS = ["#198754", "#0d6efd", "#6f42c1", "#20c997", "#fd7e14", "#0dcaf0"];
const PIE_COLORS = ["#198754", "#0d6efd", "#20c997", "#6f42c1", "#fd7e14", "#0dcaf0", "#ffc107", "#e83e8c"];
const PAGE_SIZE = 20;
const unwrap = (res) => res?.data?.data ?? res?.data ?? {};
const DEFAULT_PERIOD = "MONTH";
const isCanceled = (err) =>
  err?.code === "ERR_CANCELED" || err?.name === "CanceledError" || err?.name === "AbortError";

const buildDefaultFilters = () => ({
  customerCode: "",
  lineId: "",
  period: DEFAULT_PERIOD,
  ...buildPeriodParams(DEFAULT_PERIOD),
});

const sanitizeInitial = (init) => {
  if (!init) return {};
  const out = {};
  ["customerCode", "lineId", "period", "dateFrom", "dateTo"].forEach((k) => {
    if (init[k] != null && init[k] !== "") out[k] = String(init[k]);
  });
  return out;
};

const buildInitialChartMetric = (filters, lineOptions = []) => {
  if (!filters?.lineId) return { type: "total" };
  const line = lineOptions.find((l) => String(l.id) === String(filters.lineId));
  return {
    type: "line",
    id: filters.lineId,
    name: line?.name || line?.lineName || `Line ${filters.lineId}`,
  };
};

const RevenueList = ({ embedded = false, initialFilters = null }) => {
  const buildInitialFilters = () => ({ ...buildDefaultFilters(), ...sanitizeInitial(initialFilters) });

  const [rows, setRows] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  // { type: "total" } | { type: "orders" } | { type: "line", id, name }
  const [chartMetric, setChartMetric] = useState({ type: "total" });

  const [lines, setLines] = useState([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initial = useMemo(() => buildInitialFilters(), []);
  const [filterCustomerCode, setFilterCustomerCode] = useState(initial.customerCode || "");
  const [filterLine, setFilterLine] = useState(initial.lineId || "");
  const [filterFrom, setFilterFrom] = useState(initial.dateFrom || "");
  const [filterTo, setFilterTo] = useState(initial.dateTo || "");
  const [period, setPeriod] = useState(initial.period || DEFAULT_PERIOD);
  const [appliedFilters, setAppliedFilters] = useState(initial);

  const [totals, setTotals] = useState(null);
  const [trend, setTrend] = useState([]);
  const [scopedTrend, setScopedTrend] = useState([]);
  const [scopedTrendLoading, setScopedTrendLoading] = useState(false);
  const [checkingData, setCheckingData] = useState(null);
  const [tthReviewData, setTthReviewData] = useState(null);

  const fetchSeqRef = useRef(0);
  const abortRef = useRef(null);
  const scopedTrendAbortRef = useRef(null);
  const filtersRef = useRef(appliedFilters);
  filtersRef.current = appliedFilters;

  const chipDefs = useMemo(() => [
    {
      key: "period",
      label: "Thời gian",
      isActive: (v) => v && v !== DEFAULT_PERIOD,
      formatValue: (v, applied) => (
        v === "CUSTOM"
          ? formatDateChip(applied.dateFrom, applied.dateTo)
          : PERIOD_OPTIONS.find((o) => o.value === v)?.label || v
      ),
    },
    { key: "customerCode", label: "Đối tượng thu", isActive: (v) => Boolean(v) },
    {
      key: "lineId",
      label: "Line KD",
      isActive: (v) => Boolean(v),
      formatValue: (v) => resolveOptionLabel(v, lines),
    },
  ], [lines]);

  const activeChips = useMemo(
    () => buildChips(appliedFilters, chipDefs),
    [appliedFilters, chipDefs]
  );

  const buildApiParams = (filters, p = 0) => {
    const params = { page: p, size: PAGE_SIZE, period: filters.period || DEFAULT_PERIOD };
    if (filters.customerCode) params.customerCode = filters.customerCode;
    if (filters.lineId) params.lineId = filters.lineId;
    if (filters.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters.dateTo) params.dateTo = filters.dateTo;
    return params;
  };

  const buildTableParams = (filters, p, metric) => {
    const params = buildApiParams(filters, p);
    if (metric?.type === "line" && metric.id != null) params.lineId = metric.id;
    return params;
  };

  const fetchTable = useCallback(async (p, filters, metric) => {
    const seq = ++fetchSeqRef.current;
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    try {
      const params = buildTableParams(filters, p, metric);
      const res = await axiosInstance.get("/api/accounting/revenues", { params, signal: controller.signal });
      if (seq !== fetchSeqRef.current) return;
      const body = unwrap(res);
      setRows(body.content || []);
      setTotalPages(body.totalPages || 0);
      setTotalElements(body.totalElements || 0);
      setPage(p);
    } catch (err) {
      if (isCanceled(err) || seq !== fetchSeqRef.current) return;
      toast.error("Không thể tải dữ liệu doanh thu");
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  }, []);

  const fetchData = useCallback(async (p = 0, filters, metric = { type: "total" }) => {
    const next = filters || filtersRef.current;
    const seq = ++fetchSeqRef.current;
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    try {
      const tableParams = buildTableParams(next, p, metric);
      const listParams = buildApiParams(next, 0);
      delete listParams.page;
      delete listParams.size;
      const [res, totalsRes, trendRes] = await Promise.all([
        axiosInstance.get("/api/accounting/revenues", { params: tableParams, signal: controller.signal }),
        axiosInstance.get("/api/accounting/revenues/summary/totals", { params: listParams, signal: controller.signal }),
        axiosInstance.get("/api/accounting/revenues/trend", { params: listParams, signal: controller.signal }),
      ]);
      if (seq !== fetchSeqRef.current) return;
      const body = unwrap(res);
      setRows(body.content || []);
      setTotalPages(body.totalPages || 0);
      setTotalElements(body.totalElements || 0);
      setTotals(unwrap(totalsRes));
      const trendBody = unwrap(trendRes);
      setTrend(Array.isArray(trendBody) ? trendBody : []);
      setPage(p);
    } catch (err) {
      if (isCanceled(err) || seq !== fetchSeqRef.current) return;
      toast.error("Không thể tải dữ liệu doanh thu");
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const lRes = await axiosInstance.get("/api/accounting/revenues/lines").catch(() => null);
      const lineData = unwrap(lRes);
      const loadedLines = Array.isArray(lineData) ? lineData : [];
      if (cancelled) return;
      setLines(loadedLines);

      const defaults = buildInitialFilters();
      const metric = buildInitialChartMetric(defaults, loadedLines);
      setAppliedFilters(defaults);
      syncDraft(defaults);
      if (defaults.lineId) {
        setChartMetric(metric);
      }
      fetchData(0, defaults, metric);
    })();
    return () => {
      cancelled = true;
      if (abortRef.current) abortRef.current.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (chartMetric.type === "total") return undefined;
    if (scopedTrendAbortRef.current) scopedTrendAbortRef.current.abort();
    const controller = new AbortController();
    scopedTrendAbortRef.current = controller;
    setScopedTrendLoading(true);

    const params = buildApiParams(appliedFilters, 0);
    delete params.page;
    delete params.size;
    if (chartMetric.type === "orders") {
      params.trendMetric = "ORDERS";
    } else if (chartMetric.type === "line") {
      params.lineId = chartMetric.id;
    }

    axiosInstance.get("/api/accounting/revenues/trend", { params, signal: controller.signal })
      .then((res) => {
        const body = unwrap(res);
        setScopedTrend(Array.isArray(body) ? body : []);
      })
      .catch((err) => {
        if (isCanceled(err)) return;
        toast.error("Không thể tải biểu đồ");
      })
      .finally(() => setScopedTrendLoading(false));
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartMetric, appliedFilters]);

  const selectMetric = (metric) => {
    if (metric.type === "line") {
      if (chartMetric.type === "line" && String(chartMetric.id) === String(metric.id)) {
        const next = { type: "total" };
        setChartMetric(next);
        fetchTable(0, appliedFilters, next);
        return;
      }
    }
    setChartMetric(metric);
    fetchTable(0, appliedFilters, metric);
  };

  const applyFilters = (next) => {
    setAppliedFilters(next);
    filtersRef.current = next;
    setChartMetric({ type: "total" });
    setTotals(null);
    setTrend([]);
    setScopedTrend([]);
    setRows([]);
    fetchData(0, next, { type: "total" });
  };

  const syncDraft = (filters) => {
    setFilterCustomerCode(filters.customerCode || "");
    setFilterLine(filters.lineId || "");
    setPeriod(filters.period || DEFAULT_PERIOD);
    setFilterFrom(filters.dateFrom || "");
    setFilterTo(filters.dateTo || "");
  };

  const buildNextFilters = () => {
    const range = period === "CUSTOM"
      ? { dateFrom: filterFrom, dateTo: filterTo }
      : buildPeriodParams(period);
    return {
      customerCode: filterCustomerCode.trim(),
      lineId: filterLine,
      period,
      dateFrom: range.dateFrom || "",
      dateTo: range.dateTo || "",
    };
  };

  const handleSearch = () => applyFilters(buildNextFilters());
  const handleReset = () => {
    const defaults = buildDefaultFilters();
    syncDraft(defaults);
    applyFilters(defaults);
  };
  const handleRemoveChip = (key) => {
    const base = { ...appliedFilters };
    if (key === "period") {
      const defaults = buildDefaultFilters();
      Object.assign(base, defaults);
      base.customerCode = appliedFilters.customerCode;
      base.lineId = appliedFilters.lineId;
    } else {
      base[key] = "";
    }
    syncDraft(base);
    applyFilters(base);
  };

  const exportExcel = () => {
    if (!rows.length) { toast.warn("Không có dữ liệu để xuất"); return; }
    const ws = XLSX.utils.json_to_sheet(
      rows.map((r, i) => ({
        STT: i + 1 + page * PAGE_SIZE,
        "Mã đối tượng thu": r.customerCode || "",
        "Mã phiếu thu": r.voucherCode || "",
        "Ngày thu": r.reconciliationDate || "",
        "Line KD": r.lineName || "",
        "Doanh thu": r.totalAmount || 0,
        "SL đơn": r.orderCount || 0,
      }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "DoanhThu");
    XLSX.writeFile(wb, "DoanhThu.xlsx");
  };


  const periodLabel = getPeriodLabel(appliedFilters.period);
  const rangeHint = appliedFilters.dateFrom && appliedFilters.dateTo
    ? `${appliedFilters.dateFrom} → ${appliedFilters.dateTo}`
    : "";
  const byLine = useMemo(() => totals?.byLine || [], [totals]);

  const pieLine = useMemo(() => {
    const total = byLine.reduce((s, l) => s + Number(l.amount || 0), 0);
    return byLine
      .filter((l) => Number(l.amount || 0) > 0)
      .map((l) => ({
        id: l.lineId,
        name: l.lineName || "Không line",
        value: Number(l.amount || 0),
        sharePct: total > 0 ? (Number(l.amount || 0) / total) * 100 : 0,
      }));
  }, [byLine]);

  const pieSliceLabel = ({ percent }) => `${((Number(percent) || 0) * 100).toFixed(0)}%`;
  const pieLegendFormatter = (value, entry) => {
    const pct = entry?.payload?.sharePct;
    if (pct == null || Number.isNaN(Number(pct))) return value;
    return `${value} (${Number(pct).toFixed(1)}%)`;
  };
  const pieTooltipFormatter = (v, _n, item) => {
    const pct = item?.payload?.sharePct;
    const pctText = pct != null ? ` (${Number(pct).toFixed(1)}%)` : "";
    return [`${fmtVND(v)}${pctText}`, item?.payload?.name || ""];
  };

  const renderPie = () => (
    <Card className="mb-3" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
      <Card.Body>
        <h6 className="fw-bold mb-3">Cơ cấu doanh thu theo Line KD</h6>
        {pieLine.length > 0 ? (
          <div className="acc-chart-wrap acc-chart-wrap-lg">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart margin={{ top: 12, right: 12, bottom: 8, left: 12 }}>
                <Pie
                  data={pieLine}
                  cx="50%"
                  cy="46%"
                  innerRadius={56}
                  outerRadius={92}
                  paddingAngle={2}
                  dataKey="value"
                  nameKey="name"
                  label={pieSliceLabel}
                  labelLine={{ strokeWidth: 1 }}
                  onClick={(entry) => {
                    if (entry?.id != null) {
                      selectMetric({
                        type: "line",
                        id: entry.id,
                        name: entry.name || `Line ${entry.id}`,
                      });
                    }
                  }}
                  style={{ cursor: "pointer" }}
                >
                  {pieLine.map((_, i) => (
                    <Cell key={`slice-${i}`} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={pieTooltipFormatter} />
                <Legend
                  verticalAlign="bottom"
                  formatter={pieLegendFormatter}
                  wrapperStyle={{ paddingTop: 8, fontSize: 12 }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="text-center text-muted py-5">Không có dữ liệu</div>
        )}
      </Card.Body>
    </Card>
  );

  const renderChartArea = () => {
    if (chartMetric.type === "total") {
      return (
        <>
          <PeriodCompareLineChart
            data={trend}
            title={`Biểu đồ doanh thu — ${periodLabel} vs kỳ trước`}
            currentLabel="Kỳ này"
            previousLabel="Kỳ trước"
            currentColor="#198754"
          />
          {renderPie()}
        </>
      );
    }
    if (scopedTrendLoading) {
      return (
        <Card className="mb-3">
          <Card.Body className="text-center py-4">
            <Spinner animation="border" size="sm" className="me-2" />Đang tải biểu đồ…
          </Card.Body>
        </Card>
      );
    }
    const title = chartMetric.type === "orders"
      ? `Biểu đồ số đơn — ${periodLabel} vs kỳ trước`
      : `Biểu đồ doanh thu — ${chartMetric.name || "Line"} — ${periodLabel} vs kỳ trước`;
    return (
      <PeriodCompareLineChart
        data={scopedTrend}
        title={title}
        currentLabel="Kỳ này"
        previousLabel="Kỳ trước"
        currentColor={chartMetric.type === "orders" ? "#0d6efd" : "#198754"}
        valueFormat={chartMetric.type === "orders" ? "number" : "money"}
      />
    );
  };

  const body = (
    <div className={embedded ? "" : "acc-page"}>
      <Card className="mb-3 acc-filter-card">
        <Card.Body className="p-0">
          <AccountingFilterSection
            title={!embedded ? "Báo cáo Doanh thu" : undefined}
            open={filterOpen}
            onToggle={setFilterOpen}
            onSubmit={handleSearch}
            onReset={handleReset}
            activeCount={countActiveChips(activeChips)}
            toolbarRight={(
              <Button variant="success" size="sm" onClick={exportExcel} disabled={loading || !rows.length}>
                <FaFileExcel className="me-1" /> Xuất Excel
              </Button>
            )}
          >
            <Row className="g-2 align-items-end acc-filter-grid">
              <Col>
                <Form.Label className="small mb-1">Thời gian</Form.Label>
                <Form.Select size="sm" value={period} onChange={(e) => setPeriod(e.target.value)}>
                  {PERIOD_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </Form.Select>
              </Col>
              {period === "CUSTOM" && (
                <>
                  <Col>
                    <Form.Label className="small mb-1">Từ ngày</Form.Label>
                    <Form.Control type="date" size="sm" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} />
                  </Col>
                  <Col>
                    <Form.Label className="small mb-1">Đến ngày</Form.Label>
                    <Form.Control type="date" size="sm" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} />
                  </Col>
                </>
              )}
              <Col>
                <Form.Label className="small mb-1">Mã đối tượng thu</Form.Label>
                <Form.Control
                  type="text"
                  size="sm"
                  placeholder="Nhập mã đối tượng thu..."
                  value={filterCustomerCode}
                  onChange={(e) => setFilterCustomerCode(e.target.value)}
                />
              </Col>
              <Col>
                <Form.Label className="small mb-1">Line KD</Form.Label>
                <Form.Select size="sm" value={filterLine} onChange={(e) => setFilterLine(e.target.value)}>
                  <option value="">Tất cả</option>
                  {lines.map((l) => (
                    <option key={l.id} value={l.id}>{l.name || l.lineName}</option>
                  ))}
                </Form.Select>
              </Col>
            </Row>
          </AccountingFilterSection>
          <AccountingActiveFilters chips={activeChips} onRemove={handleRemoveChip} onClearAll={handleReset} />
        </Card.Body>
      </Card>

      {rangeHint && (
        <div className="text-muted small mb-2">
          Kỳ báo cáo: <strong>{periodLabel}</strong> ({rangeHint})
        </div>
      )}

      <div className="acc-summary-grid--equal">
        <div className="acc-summary-card-wrap">
          <ReportSummaryCard
            title="Tổng doanh thu"
            value={totals?.totalAmount}
            color="#198754"
            icon={FaMoneyBillWave}
            loading={loading}
            compact
            onClick={() => selectMetric({ type: "total" })}
            active={chartMetric.type === "total"}
          />
        </div>
        <div className="acc-summary-card-wrap">
          <ReportSummaryCard
            title="Tổng đơn"
            value={totals?.orderCount}
            color="#0d6efd"
            icon={FaShoppingCart}
            loading={loading}
            compact
            format="number"
            onClick={() => selectMetric({ type: "orders" })}
            active={chartMetric.type === "orders"}
          />
        </div>
        {byLine.map((l, idx) => (
          <div key={l.lineId ?? l.lineName ?? idx} className="acc-summary-card-wrap">
            <ReportSummaryCard
              title={l.lineName || "Không line"}
              value={l.amount}
              color={LINE_CARD_COLORS[idx % LINE_CARD_COLORS.length]}
              icon={FaLayerGroup}
              loading={loading}
              compact
              subtitle={l.orderCount != null ? `${formatMoney(l.orderCount)} đơn` : "Doanh thu theo line"}
              onClick={() => selectMetric({
                type: "line",
                id: l.lineId,
                name: l.lineName || "Không line",
              })}
              active={chartMetric.type === "line" && String(chartMetric.id) === String(l.lineId)}
            />
          </div>
        ))}
      </div>

      {loading && !totals ? (
        <Card className="mb-3"><Card.Body className="text-center py-4"><Spinner animation="border" size="sm" className="me-2" />Đang tải…</Card.Body></Card>
      ) : (
        renderChartArea()
      )}

      <Card style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : rows.length === 0 ? (
            <div className="text-center py-5 text-muted">Không có dữ liệu doanh thu trong kỳ đã chọn</div>
          ) : (
            <>
              <div className="acc-table-wrap">
                <Table striped hover className="mb-0 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 50 }}>STT</th>
                      <th>Đối tượng thu</th>
                      <th>Mã phiếu thu</th>
                      <th className="acc-col-hide-sm">Ngày thu</th>
                      <th>Line KD</th>
                      <th className="text-end acc-col-hide-sm">SL đơn</th>
                      <th className="text-end">Doanh thu</th>
                      <th className="text-center" style={{ width: 60 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, idx) => (
                      <tr key={`${r.id}-${r.lineId}-${idx}`}>
                        <td>{idx + 1 + page * PAGE_SIZE}</td>
                        <td className="text-muted">{r.customerCode || "—"}</td>
                        <td>{r.voucherCode || "—"}</td>
                        <td className="acc-col-hide-sm">{r.reconciliationDate || "—"}</td>
                        <td>{r.lineName || "—"}</td>
                        <td className="text-end acc-col-hide-sm">{r.orderCount ?? 0}</td>
                        <td className="text-end fw-semibold text-success">{fmtVND(r.totalAmount)}</td>
                        <td className="text-center">
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-primary"
                            title="Xem chi tiết"
                            onClick={() => {
                              if (!r.invoiceId || Number(r.invoiceId) <= 0) {
                                toast.warn("Phiếu không có liên kết đối soát.");
                                return;
                              }
                              // invoiceId của dòng TTH là TthInvoice.id — 1 ID space độc lập với
                              // Invoice.id mà CheckingReview đang giả định (mở nhầm sẽ ra rỗng/sai).
                              if (r.sourceType === "TTH_RECONCILIATION") {
                                setTthReviewData({ id: r.invoiceId });
                              } else {
                                setCheckingData({ id: r.invoiceId, money: r.totalAmount, lineId: r.lineId });
                              }
                            }}
                          >
                            <FaEye />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              <div className="acc-table-footer">
                <small className="text-muted">Tổng: {totalElements} dòng</small>
                <Pagination
                  hidePaginationWhenSinglePage
                  showItemsPerPage={false}
                  pageData={{ page: { number: page, totalPages, totalElements, size: PAGE_SIZE } }}
                  onPageChange={(p) => !loading && fetchTable(p - 1, appliedFilters, chartMetric)}
                />
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      {checkingData && (
        <CheckingReview
          data={checkingData}
          handleSearch={() => setCheckingData(null)}
          canEdit={false}
          canDelete={false}
          nestedModal
        />
      )}
      {tthReviewData && (
        <TthReconciliationReview
          data={tthReviewData}
          onClose={() => setTthReviewData(null)}
          canEdit={false}
          canDelete={false}
          nestedModal
        />
      )}
    </div>
  );

  if (embedded) return body;
  return <AdLayout>{body}</AdLayout>;
};

export default RevenueList;
